/**
 * P2.0 STEP C3 · K19 · Split di `lib/dates.js`.
 *
 * Foundation (CORE, prodotto-agnostico):
 *   - `formatDateIT`, `formatDateTimeIT`, `parseISOLocal`, `formatDateLongIT`,
 *     `MONTHS_IT`, `WEEKDAYS_SHORT`, `isIsoDate`
 *   sono definiti in `foundation/formatting/dates.js` — riutilizzabili da
 *   tutti i Product Pack Suite (Clienti, Studio, Fatturazione, Finance,
 *   Connect, AI Assistant, futuri moduli).
 *
 * SHARED PRODUCT (Presenze — Santo Patrono anagrafica azienda):
 *   - `mmddToItalian`, `italianToMmdd`, `_DAYS_PER_MONTH_MAX`
 *   restano fisicamente in questo file finché non verrà istanziato il
 *   perimetro `packs/gi_presenze/*` (previsto in P2.x — fuori scope P2.0).
 *
 * Questo file conserva la superficie pubblica `@/lib/dates` di 7 export per
 * i consumatori esistenti (SuiteLauncher, Footer, HourProfiles, ImportExcel,
 * ImportTabulato, Impostazioni, ModalitaGuidata, ScheduleExceptionsCard,
 * Companies, GuideShell). Nessuna modifica funzionale.
 *
 * STANDARD ufficiale Gruppo Impresa Suite:
 *   - Data:     "gg/mm/aaaa"
 *   - Ora:      "HH:mm"
 *   - Data/Ora: "gg/mm/aaaa HH:mm"
 */
import {
  formatDateIT as _formatDateIT,
  formatDateTimeIT as _formatDateTimeIT,
  parseISOLocal as _parseISOLocal,
} from "@/foundation/formatting";

/** Utility CORE — re-esportata da Foundation per retrocompatibilità. */
export { isIsoDate } from "@/foundation/formatting";

/**
 * Formatta una data in "gg/mm/aaaa" (STANDARD Suite).
 * Wrapper del formatter CORE per conservare il fallback default "—" atteso
 * dai consumatori legacy (Foundation usa `""` come default).
 */
export function formatDateIT(input, fallback = "\u2014") {
  return _formatDateIT(input, fallback);
}

/**
 * Formatta un istante in "gg/mm/aaaa HH:mm" (STANDARD Suite).
 * Wrapper del formatter CORE (fallback default "—" per retrocompat).
 */
export function formatDateTimeIT(input, fallback = "\u2014") {
  return _formatDateTimeIT(input, fallback);
}

/** Parser sicuro (timezone-free) di "YYYY-MM-DD" in un Date locale. */
export function parseISOLocal(iso) {
  return _parseISOLocal(iso);
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED PRODUCT · Santo Patrono (v0.99.30-c1)
//
// Il gestionale Presenze memorizza la data del patrono in formato tecnico
// "MM-DD" (retrocompatibilita' con l'engine festivita'). L'utente italiano lo
// compila e lo legge in formato "GG-MM". Queste utility fanno da adapter senza
// mai toccare lo storage.
//
// SCOPE: dominio Presenze (anagrafica azienda). Da spostare in
// `packs/gi_presenze/shared/lib/` quando istanzieremo il Product Pack.
// ─────────────────────────────────────────────────────────────────────────────

const _DAYS_PER_MONTH_MAX = { 1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30, 7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31 };

/** Converte "MM-DD" (interno) -> "GG-MM" (UI italiana). */
export function mmddToItalian(mmdd) {
  if (!mmdd || typeof mmdd !== "string") return "";
  const m = mmdd.match(/^(\d{2})-(\d{2})$/);
  if (!m) return "";
  const mm = m[1], dd = m[2];
  return `${dd}-${mm}`;
}

/** Converte "GG-MM" (input utente) -> "MM-DD" (storage) con validazione. */
export function italianToMmdd(ggmm) {
  if (ggmm === "" || ggmm == null) return { value: "", error: null };
  if (typeof ggmm !== "string") return { value: "", error: "Formato non valido" };
  const m = ggmm.match(/^(\d{2})-(\d{2})$/);
  if (!m) return { value: "", error: "Usa il formato GG-MM (es. 28-04)" };
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  if (mm < 1 || mm > 12) return { value: "", error: `Mese non valido: ${m[2]}` };
  const max = _DAYS_PER_MONTH_MAX[mm];
  if (dd < 1 || dd > max) return { value: "", error: `Giorno non valido per ${m[2]}: ${m[1]}` };
  return { value: `${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`, error: null };
}

// -----------------------------------------------------------------------
// Release / Manuale
//
// v1.3.0: allineato allo STANDARD ufficiale Suite (gg/mm/aaaa con slash).
// Fonte autoritativa unica: SSoT `/RELEASE_DATE`.
// -----------------------------------------------------------------------
/**
 * @deprecated Usare `formatDateIT(input)` direttamente. Alias mantenuto per
 * retrocompatibilita' con i consumer esistenti (Footer, SuiteLauncher,
 * GuideShell). Restituisce sempre lo stesso formato Standard Suite.
 */
export function formatReleaseDateIT(input, fallback = "") {
  return _formatDateIT(input, fallback);
}
